import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { CostGeneric } from '../../_models/financials/costGeneric';
import { FieldActivity } from '../../_models';

@Injectable()
export class CostGenericsService {
  private apiBaseUrl = environment.financeServiceUrl;

  constructor(private http: HttpClient) {}

  getList(): Observable<CostGeneric[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/finance/invoice/expenses')
      .pipe(map((response) => response));
  }

  getListByYear(year): Observable<CostGeneric[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/finance/invoice/expenses?cropYear=' + year)
      .pipe(map((response) => response));
  }

  get(id: number): Observable<CostGeneric> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/finance/invoice/expenses/' + id)
      .pipe(map((response) => response));
  }

  getExpensesByGroupId(id): Observable<CostGeneric[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/finance/invoice/expenses?groupId=' + id)
      .pipe(map((response) => response));
  }

  save(costGenericItem: CostGeneric | CostGeneric[]): Observable<CostGeneric | CostGeneric[]> {
    let costGenericPayload: any;

    // Controllo se il payload è un array o un singolo oggetto
    if (Array.isArray(costGenericItem)) {
      costGenericPayload = costGenericItem.map((item) => this.normalizeCostGeneric(item));
    } else {
      costGenericPayload = this.normalizeCostGeneric(costGenericItem);
    }

    if (Array.isArray(costGenericPayload)) {
      // POST: array di oggetti
      return this.http.post<CostGeneric[]>(
        `${this.apiBaseUrl}api/private/v1/finance/invoice/expenses/multi`,
        costGenericPayload
      );
    } else {
      // PUT: singolo oggetto con ID
      return this.http.put<CostGeneric>(
        `${this.apiBaseUrl}api/private/v1/finance/invoice/expenses/${costGenericPayload.id}`,
        costGenericPayload
      );
    }
  }

  /**
   * Normalizza un singolo oggetto CostGeneric
   */
  private normalizeCostGeneric(costGeneric: any): CostGeneric {
    const normalized = { ...costGeneric };

    // Rimuoviamo i campi non necessari
    delete normalized.lastUpdated;
    delete normalized.dateCreated;

    // Recupero sicuro della companyId da localStorage
    const companySelected = localStorage.getItem('companySelected');
    if (companySelected) {
      try {
        const company = JSON.parse(companySelected);
        normalized.companyId = company?.id || null;
      } catch (e) {
        console.error('Errore nel parsing di companySelected', e);
      }
    }

    // Normalizziamo il campo user
    if (normalized.user?.id) {
      normalized.user = { id: normalized.user.id };
    }

    return normalized;
  }

  patch(costGeneric): Observable<CostGeneric> {
    delete costGeneric.lastUpdated;
    delete costGeneric.dateCreated;

    return this.http
      .patch<any>(
        this.apiBaseUrl + 'api/private/v1/finance/invoice/expenses/depreciation',
        costGeneric
      )
      .pipe(map((response) => response as CostGeneric));
  }

  delete(costGeneric: CostGeneric): Observable<CostGeneric> {
    if (costGeneric.transactionDistribution === 'depreciation') {
      return this.http
        .delete<any>(
          this.apiBaseUrl + 'api/private/v1/finance/invoice/expenses/depreciation/' + costGeneric.id
        )
        .pipe(map(() => ({ id: costGeneric.id }) as CostGeneric));
    } else {
      return this.http
        .delete<any>(this.apiBaseUrl + 'api/private/v1/finance/invoice/expenses/' + costGeneric.id)
        .pipe(map(() => ({ id: costGeneric.id }) as CostGeneric));
    }
  }

  getCostForFieldActivty(ids: Array<FieldActivity['id']>, year) {
    const IdsQueryString = ids.map((id) => `id=${id}`).join('&');
    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v1/finance/metrics/kpi/field_activity?' +
          IdsQueryString +
          '&cropYear=' +
          year
      )
      .pipe(map((response) => response));
  }
}
